import { useCallback, useEffect, useRef, useState } from 'react';

const SMOOTHING_THRESHOLD_DEG = 2;

function getScreenAngle() {
  if (typeof window === 'undefined') return 0;
  const angle = window.screen?.orientation?.angle;
  if (typeof angle === 'number') return angle;
  if (typeof window.orientation === 'number') return window.orientation;
  return 0;
}

function normalizeAngle(deg) {
  const n = deg % 360;
  return n < 0 ? n + 360 : n;
}

export default function useDeviceOrientation() {
  const [heading, setHeading] = useState(null);
  const lastHeadingRef = useRef(null);
  const attachedRef = useRef(false);

  const handleEvent = useCallback((event) => {
    let raw;
    if (typeof event.webkitCompassHeading === 'number') {
      raw = event.webkitCompassHeading;
    } else if (typeof event.alpha === 'number') {
      raw = 360 - event.alpha;
    } else {
      return;
    }

    const corrected = normalizeAngle(raw + getScreenAngle());

    const prev = lastHeadingRef.current;
    if (prev != null) {
      let delta = Math.abs(corrected - prev);
      if (delta > 180) delta = 360 - delta;
      if (delta < SMOOTHING_THRESHOLD_DEG) return;
    }
    lastHeadingRef.current = corrected;
    setHeading(corrected);
  }, []);

  const attachListeners = useCallback(() => {
    if (attachedRef.current) return;
    attachedRef.current = true;
    window.addEventListener('deviceorientationabsolute', handleEvent, true);
    window.addEventListener('deviceorientation', handleEvent, true);
  }, [handleEvent]);

  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined') return false;
    const Ctor = window.DeviceOrientationEvent;
    if (Ctor && typeof Ctor.requestPermission === 'function') {
      try {
        const result = await Ctor.requestPermission();
        if (result === 'granted') {
          attachListeners();
          return true;
        }
        return false;
      } catch {
        return false;
      }
    }
    attachListeners();
    return true;
  }, [attachListeners]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const Ctor = window.DeviceOrientationEvent;
    const needsPermission =
      Ctor && typeof Ctor.requestPermission === 'function';
    if (!needsPermission) {
      attachListeners();
    }
    return () => {
      if (attachedRef.current) {
        window.removeEventListener('deviceorientationabsolute', handleEvent, true);
        window.removeEventListener('deviceorientation', handleEvent, true);
        attachedRef.current = false;
      }
    };
  }, [attachListeners, handleEvent]);

  return { heading, requestPermission };
}
